import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck, Loader2 } from "lucide-react";
import { Logo } from "../../components/Brand";
import { api, errMsg, PLANS, useSEO } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const Shell = ({ title, sub, children, wide }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-white">
    <div className="hidden lg:flex flex-col justify-between bg-[#0A192F] text-white p-12 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <Link to="/" className="relative"><Logo light /></Link>
      <div className="relative">
        <h2 className="text-4xl font-bold leading-tight">Quality. Safety. Compliance.<br />All in One Place.</h2>
        <p className="mt-4 text-slate-300 max-w-md">Integrated Quality, Safety &amp; Compliance Management Software for Australian businesses.</p>
      </div>
      <p className="relative text-xs text-slate-500">© {new Date().getFullYear()} QORDEL 360</p>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12">
      <div className={`w-full ${wide ? "max-w-xl" : "max-w-md"}`}>
        <Link to="/" className="lg:hidden inline-block mb-8"><Logo /></Link>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        {sub && <p className="mt-2 text-slate-600">{sub}</p>}
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

const Field = ({ label, testid, ...p }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    <input {...p} className="field mt-1.5" data-testid={testid} />
  </label>
);

const Submit = ({ busy, children, testid }) => (
  <button disabled={busy} className="w-full bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white font-semibold py-3 rounded-md transition-colors inline-flex justify-center items-center gap-2" data-testid={testid}>
    {busy && <Loader2 className="w-4 h-4 animate-spin" />}{children}
  </button>
);

const Err = ({ msg }) => (msg ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="auth-error">{msg}</div> : null);

export function Login() {
  useSEO("Log in");
  const { setSession } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = typeof loc.state?.from === "string" && loc.state.from.startsWith("/") && !loc.state.from.startsWith("//") ? loc.state.from : null;
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/login", f);
      setSession(data);
      nav(data.user.role === "platform_owner" ? "/admin" : (from || "/app"));
    } catch (e2) {
      setErr(errMsg(e2));
    }
    setBusy(false);
  };
  const resend = async () => {
    await api.post("/auth/resend-verification", { email: f.email }).catch(() => {});
    toast.success("If your account is unverified, a new verification email has been sent.");
  };
  return (
    <Shell title="Log in to QORDEL 360" sub="Welcome back.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} testid="login-email-input" autoComplete="email" />
        <Field label="Password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="login-password-input" autoComplete="current-password" />
        <Err msg={err} />
        {err.includes("verify") && <button type="button" onClick={resend} className="text-sm font-semibold text-sky-700" data-testid="login-resend-verification">Resend verification email</button>}
        <Submit busy={busy} testid="login-submit-btn">Log in</Submit>
        <div className="flex justify-between text-sm">
          <Link to="/forgot-password" className="text-sky-700 font-medium" data-testid="login-forgot-link">Forgot password?</Link>
          <Link to="/register" className="text-slate-600">New here? <span className="text-sky-700 font-medium">Start free trial</span></Link>
        </div>
      </form>
    </Shell>
  );
}

export function Register() {
  useSEO("Start your free trial", "Register your organisation for a 5-day free trial of QORDEL 360. No credit card required.");
  const [params] = useSearchParams();
  const [f, setF] = useState({
    org_name: "", admin_name: "", email: "", password: "", phone: "", address: "", abn: "",
    selected_plan: PLANS[params.get("plan")] ? params.get("plan") : "monthly", users: Number(params.get("users")) || 1,
    referral_code: (params.get("ref") || localStorage.getItem("q360_ref") || "").toUpperCase().slice(0, 20),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/register", { ...f, users: Number(f.users) || 1 });
      setDone(data.email);
    } catch (e2) {
      setErr(errMsg(e2));
    }
    setBusy(false);
  };
  if (done) {
    return (
      <Shell title="Check your email" sub="One more step to activate your trial.">
        <div className="border border-slate-200 rounded-xl p-6" data-testid="register-success">
          <MailCheck className="w-8 h-8 text-sky-600" />
          <p className="mt-4 text-slate-700">We sent a verification link to <strong>{done}</strong>. Click the link to verify your email, create your private workspace and start your 5-day free trial.</p>
          <button onClick={() => api.post("/auth/resend-verification", { email: done }).then(() => toast.success("Verification email re-sent"))} className="mt-4 text-sm font-semibold text-sky-700" data-testid="register-resend-btn">Resend email</button>
        </div>
      </Shell>
    );
  }
  return (
    <Shell title="Start your 5-day free trial" sub="Full access for one organisation and one user. No credit card required." wide>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Organisation name" required value={f.org_name} onChange={set("org_name")} testid="register-org-input" />
          <Field label="Your name (administrator)" required value={f.admin_name} onChange={set("admin_name")} testid="register-name-input" />
          <Field label="Business email" type="email" required value={f.email} onChange={set("email")} testid="register-email-input" />
          <Field label="Password" type="password" required minLength={8} value={f.password} onChange={set("password")} testid="register-password-input" placeholder="8+ characters, letters & numbers" />
          <Field label="Phone" value={f.phone} onChange={set("phone")} testid="register-phone-input" />
          <Field label="ABN (optional)" value={f.abn} onChange={set("abn")} testid="register-abn-input" />
        </div>
        <Field label="Business address" value={f.address} onChange={set("address")} testid="register-address-input" />
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="text-sm font-medium text-slate-700">Plan after trial</span>
            <select value={f.selected_plan} onChange={set("selected_plan")} className="field mt-1.5" data-testid="register-plan-select">
              {Object.entries(PLANS).map(([k, p]) => <option key={k} value={k}>{p.label} — ${p.base} {p.period}</option>)}
            </select></label>
          <Field label="Number of users" type="number" min={1} max={100} value={f.users} onChange={set("users")} testid="register-users-input" />
        </div>
        <Field label="Referral code (optional)" value={f.referral_code} onChange={(e) => setF({ ...f, referral_code: e.target.value.toUpperCase() })} testid="register-referral-input" />
        <p className="text-xs text-slate-500">The trial includes one user. You choose and pay for your plan only if you decide to subscribe. By registering you agree to the <Link to="/legal/terms" className="underline">Terms</Link> and <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.</p>
        <Err msg={err} />
        <Submit busy={busy} testid="register-submit-btn">Create my organisation</Submit>
        <p className="text-sm text-slate-600 text-center">Already registered? <Link to="/login" className="text-sky-700 font-medium">Log in</Link></p>
      </form>
    </Shell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [err, setErr] = useState("");
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) {
      return;
    }
    ran.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then(({ data }) => {
        setSession(data);
        toast.success("Email verified — your free trial has started");
        nav("/app");
      })
      .catch((e) => setErr(errMsg(e)));
  }, [params, nav, setSession]);
  return (
    <Shell title="Verifying your email" sub="Setting up your private workspace…">
      {err ? <><Err msg={err} /><Link to="/login" className="mt-4 inline-block text-sky-700 font-semibold">Go to login</Link></> : <Loader2 className="w-6 h-6 animate-spin text-sky-600" data-testid="verify-loading" />}
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) {
      toast.error(errMsg(e2));
    }
    setBusy(false);
  };
  return (
    <Shell title="Reset your password" sub="We'll email you a secure reset link.">
      {sent ? <p className="text-slate-700" data-testid="forgot-success">If an account exists for {email}, a reset link has been sent. It expires in 1 hour.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} testid="forgot-email-input" />
          <Submit busy={busy} testid="forgot-submit-btn">Send reset link</Submit>
          <Link to="/login" className="block text-sm text-sky-700 font-medium">Back to login</Link>
        </form>
      )}
    </Shell>
  );
}

export function SetPassword({ mode }) {
  const [params] = useSearchParams();
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const invite = mode === "invite";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(invite ? "/auth/accept-invite" : "/auth/reset-password", { token: params.get("token") || "", password: pw });
      if (invite) {
        setSession(data);
        nav("/app");
      } else {
        toast.success("Password updated. Please log in.");
        nav("/login");
      }
    } catch (e2) {
      setErr(errMsg(e2));
    }
    setBusy(false);
  };
  return (
    <Shell title={invite ? "Accept your invitation" : "Choose a new password"} sub={invite ? "Set a password for your individual QORDEL 360 login." : ""}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="New password" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} testid="set-password-input" placeholder="8+ characters, letters & numbers" />
        <Err msg={err} />
        <Submit busy={busy} testid="set-password-submit-btn">{invite ? "Activate my account" : "Update password"}</Submit>
      </form>
    </Shell>
  );
}
